"use client";

import {
  type ReactNode,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import { clickableText, cx } from "@/lib/tailwind-utils";

// `as const` keeps the keys as the literal union 1 | 2 | ... | 6 so `clampLines`
// only accepts supported values; a plain `Record<number, string>` would widen
// the keys to `number` and let an unsupported value resolve to `undefined`.
const CLAMP_CLASS = {
  1: "line-clamp-1",
  2: "line-clamp-2",
  3: "line-clamp-3",
  4: "line-clamp-4",
  5: "line-clamp-5",
  6: "line-clamp-6",
} as const;

interface ExpandableTextProps {
  text: string;
  /** Lines to show before clamping. Defaults to 2. */
  clampLines?: keyof typeof CLAMP_CLASS;
  className?: string;
  /** Class for the text itself (size, colour). */
  textClassName?: string;
  /** Renders the text, e.g. to turn URLs into links. Defaults to plain text. */
  renderText?: (text: string) => ReactNode;
  /** Keeps the toggle's click from reaching a clickable parent row. */
  stopPropagation?: boolean;
}

/**
 * Renders text clamped to `clampLines`, revealing a "See more"/"See less"
 * toggle only when the text is actually cut off. Overflow is measured from the
 * DOM (rather than a character count) so the toggle appears exactly when the
 * text spills past the clamp, regardless of container width or wrapping.
 */
export function ExpandableText({
  text,
  clampLines = 2,
  className,
  textClassName,
  renderText,
  stopPropagation = false,
}: ExpandableTextProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const textRef = useRef<HTMLParagraphElement>(null);
  const contentId = useId();

  // Measure before paint (so the toggle is present on first paint, no flicker)
  // on mount, when the text changes, and whenever the element resizes (e.g.
  // window/column width changes). We only trust the measurement while collapsed
  // — once expanded the clamp is off, so scrollHeight equals clientHeight and
  // the toggle would incorrectly disappear.
  useLayoutEffect(() => {
    const el = textRef.current;
    if (!el) return;

    const measure = () => {
      if (isExpanded) return;
      setIsOverflowing(el.scrollHeight > el.clientHeight + 1);
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [text, isExpanded]);

  return (
    <div className={className}>
      <p
        id={contentId}
        ref={textRef}
        className={cx(
          "whitespace-pre-wrap break-words",
          textClassName,
          !isExpanded && CLAMP_CLASS[clampLines],
        )}
      >
        {renderText ? renderText(text) : text}
      </p>
      {(isOverflowing || isExpanded) && (
        <button
          type="button"
          onClick={(e) => {
            if (stopPropagation) e.stopPropagation();
            setIsExpanded((prev) => !prev);
          }}
          aria-expanded={isExpanded}
          aria-controls={contentId}
          className={cx(clickableText, "mt-0.5 text-caption")}
        >
          {isExpanded ? "See less" : "See more"}
        </button>
      )}
    </div>
  );
}
